import { useCallback, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { setTheme } from 'ducks/layout/layoutSlice';
import { PATHS } from 'routes/routes.constants';

import ContentSwitcher from '@polyai/ui/components/atoms/ContentSwitcher';
import {
  ChevronRightIcon,
  LogoutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
  UserIcon,
} from '@polyai/ui/components/atoms/Icons';
import Popover, { PopoverTrigger } from '@polyai/ui/components/atoms/Popover';
import { THEME_NAMES } from '@polyai/ui/styles/config/theme.constants';

import AccountModal from 'components/molecules/AccountModal';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useUserAccess } from 'hooks/useProjectResources/useUserAccess';
import * as Styled from './Menu.styled';

const truncateEmail = (email?: string) => {
  if (!email) {
    return 'User menu';
  }
  if (email.length < 20) {
    return email;
  }

  return `${email.slice(0, 17)}...`;
};

type UserMenuProps = {
  isExpanded: boolean;
  open?: boolean;
  onPopoverOpenChange?: (open: boolean) => void;
};

const UserMenu = ({ isExpanded, open, onPopoverOpenChange }: UserMenuProps) => {
  const dispatch = useLocalDispatch();
  const logout = useAppSelector(state => state.auth.logout);
  const currentTheme = useAppSelector(state => state.layout.theme);
  const { isInternalUser } = useUserAccess();
  const { user } = useAuth0();
  const { isFeatureOn } = useIsFeatureOn();
  const isModalOpenRef = useRef(false);

  const isDarkModeEnabled = isFeatureOn('dark_mode');

  const handlePopoverOpenChange = useCallback(
    (isOpen: boolean) => {
      // Don't close the popover if a modal inside it is open
      if (!isOpen && isModalOpenRef.current) {
        return;
      }
      onPopoverOpenChange?.(isOpen);
    },
    [onPopoverOpenChange],
  );

  const handleThemeChange = useCallback(
    (theme: THEME_NAMES) => {
      dispatch(setTheme(theme));
    },
    [dispatch],
  );

  const themeOptions = isDarkModeEnabled
    ? [
        {
          value: THEME_NAMES.NEW,
          icon: <SunIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to light theme',
        },
        {
          value: THEME_NAMES.DARK,
          icon: <MoonIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to dark theme',
        },
        {
          value: THEME_NAMES.SYSTEM,
          icon: <MonitorIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to system theme',
        },
      ]
    : [];

  return (
    <Popover
      open={open}
      placement="right-end"
      trigger="hover"
      useDismissProps={{ outsidePress: false }}
      onOpenChange={handlePopoverOpenChange}
    >
      <PopoverTrigger asChild>
        <Styled.FadeButton
          $isExpanded={isExpanded}
          aria-label="User menu"
          data-test-id="user-menu-button"
          endAdornment={ChevronRightIcon}
          label={truncateEmail(user?.email)}
          padding="iconButton"
          startAdornment={UserIcon}
          type="button"
        />
      </PopoverTrigger>
      <Styled.UserMenuPopoverContent closeOnFocusOut={false} initialFocus={-1}>
        <AccountModal
          trigger={
            <Styled.PopoverRow
              as="button"
              data-test-id="account-button"
              type="button"
            >
              <Styled.RowLabel>Account</Styled.RowLabel>
            </Styled.PopoverRow>
          }
          onOpen={() => {
            isModalOpenRef.current = true;
          }}
          onClose={() => {
            isModalOpenRef.current = false;
          }}
        />
        {isInternalUser && (
          <Styled.PopoverRow as="a" href={PATHS.ADMIN_PAGE}>
            <Styled.RowLabel>Admin</Styled.RowLabel>
            <Styled.RowDivider />
          </Styled.PopoverRow>
        )}
        {isDarkModeEnabled && themeOptions.length > 0 && (
          <Styled.ThemeRow>
            <Styled.RowLabel>Theme</Styled.RowLabel>
            <ContentSwitcher
              ariaLabel="Theme picker"
              options={themeOptions}
              size="small"
              value={currentTheme}
              inverse
              onChange={handleThemeChange}
            />
            <Styled.RowDivider />
          </Styled.ThemeRow>
        )}
        <Styled.PopoverRow
          as="button"
          type="button"
          onClick={() => (logout as () => void)?.()}
        >
          <Styled.RowLabel>Log out</Styled.RowLabel>
          <LogoutIcon color="iconSecondaryInverse" size="sm" />
        </Styled.PopoverRow>
      </Styled.UserMenuPopoverContent>
    </Popover>
  );
};

export default UserMenu;
